import { useEffect, useRef } from "react";

interface Props { theme: "dark" | "light"; lang: "fr" | "en" | "ar"; }

const content = {
  fr: {
    overline: "COMMENT ÇA MARCHE",
    h2: "En 3 étapes simples",
    sub: "Trouvez du carburant en quelques secondes, où que vous soyez en Mauritanie.",
    steps: [
      { num: "01", emoji: "📍", color: "#007AFF", bg: "rgba(0,122,255,0.12)", border: "rgba(0,122,255,0.25)", title: "Ouvrez la carte", desc: "Voyez instantanément toutes les stations autour de vous sur la carte interactive." },
      { num: "02", emoji: "⛽", color: "#34C759", bg: "rgba(52,199,89,0.12)", border: "rgba(52,199,89,0.25)", title: "Vérifiez la disponibilité", desc: "Diesel, Essence — disponible, stock faible ou épuisé — en temps réel." },
      { num: "03", emoji: "🤝", color: "#FF9500", bg: "rgba(255,149,0,0.12)", border: "rgba(255,149,0,0.25)", title: "Contribuez", desc: "Mettez à jour le statut des stations pour aider les autres conducteurs." },
    ],
  },
  en: {
    overline: "HOW IT WORKS",
    h2: "3 simple steps",
    sub: "Find fuel in seconds, wherever you are in Mauritania.",
    steps: [
      { num: "01", emoji: "📍", color: "#007AFF", bg: "rgba(0,122,255,0.12)", border: "rgba(0,122,255,0.25)", title: "Open the map", desc: "Instantly see all stations around you on the interactive map." },
      { num: "02", emoji: "⛽", color: "#34C759", bg: "rgba(52,199,89,0.12)", border: "rgba(52,199,89,0.25)", title: "Check availability", desc: "Diesel, Gasoline — available, low stock or out — in real time." },
      { num: "03", emoji: "🤝", color: "#FF9500", bg: "rgba(255,149,0,0.12)", border: "rgba(255,149,0,0.25)", title: "Contribute", desc: "Update station status to help other drivers on the road." },
    ],
  },
  ar: {
    overline: "كيف يعمل",
    h2: "٣ خطوات بسيطة",
    sub: "اعثر على الوقود في ثوانٍ، أينما كنت في موريتانيا.",
    steps: [
      { num: "01", emoji: "📍", color: "#007AFF", bg: "rgba(0,122,255,0.12)", border: "rgba(0,122,255,0.25)", title: "افتح الخريطة", desc: "اعرف فوراً جميع المحطات حولك على الخريطة التفاعلية." },
      { num: "02", emoji: "⛽", color: "#34C759", bg: "rgba(52,199,89,0.12)", border: "rgba(52,199,89,0.25)", title: "تحقق من التوفر", desc: "ديزل، بنزين — متاح، مخزون منخفض أو نفد — في الوقت الفعلي." },
      { num: "03", emoji: "🤝", color: "#FF9500", bg: "rgba(255,149,0,0.12)", border: "rgba(255,149,0,0.25)", title: "ساهم", desc: "حدّث حالة المحطات لمساعدة السائقين الآخرين." },
    ],
  },
};

export default function HowItWorksSection({ theme, lang }: Props) {
  const isDark = theme === "dark";
  const t = content[lang];
  const isRtl = lang === "ar";
  const sectionRef = useRef<HTMLDivElement>(null);
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textMuted = isDark ? "text-white/60" : "text-gray-500";
  const cardBg = isDark ? "bg-[rgba(28,28,30,0.85)] border-white/10" : "bg-white/85 border-black/10";

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.querySelectorAll<HTMLElement>(".step-card").forEach((card, i) => {
            setTimeout(() => { card.style.opacity = "1"; card.style.transform = "translateY(0)"; }, i * 120);
          });
        }
      });
    }, { threshold: 0.1 });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="how-it-works" className="py-28 relative" style={{ background: isDark ? "linear-gradient(180deg,#000 0%,rgba(0,122,255,0.04) 50%,#000 100%)" : "linear-gradient(180deg,#F2F2F7 0%,rgba(0,122,255,0.04) 50%,#F2F2F7 100%)" }} dir={isRtl ? "rtl" : "ltr"}>
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" ref={sectionRef}>
        <div className="text-center mb-16">
          <p className="text-[#007AFF] text-xs font-semibold tracking-[0.15em] uppercase mb-4">{t.overline}</p>
          <h2 className={`text-4xl md:text-5xl font-bold tracking-tight mb-5 ${textPrimary}`} style={{ fontFamily: "-apple-system,'SF Pro Display',system-ui,sans-serif" }}>{t.h2}</h2>
          <p className={`text-lg max-w-[480px] mx-auto leading-relaxed ${textMuted}`}>{t.sub}</p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
          {/* Dashed connector */}
          <div className="hidden md:block absolute top-12 left-[calc(33.33%+16px)] right-[calc(33.33%+16px)] h-px z-0" style={{ borderTop: "2px dashed rgba(0,122,255,0.35)" }} />

          {t.steps.map((step, i) => (
            <div key={i} className="relative">
              <div
                className={`step-card relative z-10 p-8 rounded-2xl backdrop-blur-[20px] border overflow-hidden transition-all duration-300 hover:-translate-y-1 ${cardBg}`}
                style={{ opacity: 0, transform: "translateY(24px)", transition: "opacity 0.55s ease, transform 0.55s ease" }}
              >
                {/* Big muted number behind */}
                <span className="absolute -bottom-4 right-4 text-[96px] font-black leading-none select-none pointer-events-none" style={{ color: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)", fontFamily: "-apple-system,'SF Pro Display',system-ui,sans-serif" }}>{step.num}</span>

                {/* Icon circle */}
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl mb-6 text-2xl" style={{ background: step.bg, border: `1px solid ${step.border}` }}>
                  {step.emoji}
                </div>

                {/* Step badge */}
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold mb-3" style={{ background: step.bg, border: `1px solid ${step.border}`, color: step.color }}>
                  {lang === "ar" ? `الخطوة ${step.num}` : `Étape ${step.num}`}
                </div>

                <h3 className={`text-xl font-bold mb-3 ${textPrimary}`} style={{ fontFamily: "-apple-system,'SF Pro Display',system-ui,sans-serif" }}>{step.title}</h3>
                <p className={`text-sm leading-relaxed ${textMuted}`}>{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
